import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { SettingsService } from 'src/app/services/settings.service';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { MatDialog } from '@angular/material/dialog';
import { ShowDialogsComponent } from 'src/app/components/show-dialogs/show-dialogs.component';

@Component({
  selector: 'app-saml-signon',
  template: `
    <div class="saml-page">
      <div class="content">
        <div class="pagehead">
          <h1>Single sign-on (SAML)</h1>
          <p>
            Let your team sign in with your company login instead of a separate
            password.
          </p>
        </div>

        <form
          class="panel"
          [formGroup]="formData"
          (ngSubmit)="submitForm()"
        >
          <div class="panel-b">
            <!-- Toggle row -->
            <div class="switch-row">
              <div
                class="switch"
                [class.off]="!saml_config?.is_active"
                [class.disabled]="ssoSharedAcrossTenants"
                (click)="
                  !ssoSharedAcrossTenants &&
                    activateDeactivateSso({ is_active: !saml_config?.is_active })
                "
              >
                <span class="knob"></span>
              </div>
              <div class="switch-txt">
                <div class="t">Enable single sign-on</div>
                <div class="s">
                  When on, your team signs in through your identity provider.
                </div>
              </div>
              <span class="onbadge" *ngIf="saml_config?.is_active">On</span>
              <span class="offbadge" *ngIf="!saml_config?.is_active">Off</span>
            </div>

            <!-- SSO managed globally notice -->
            <div class="amber-notice" *ngIf="ssoSharedAcrossTenants">
              <mat-icon>warning</mat-icon>
              <div>
                <p class="h">SSO is Managed Globally</p>
                <p class="s">
                  SSO configuration is shared across all tenants and managed by
                  the system administrator. You cannot modify these settings at
                  the tenant level.
                </p>
              </div>
            </div>

            <!-- Metadata URL -->
            <div class="field">
              <label for="metadataUrl">Identity provider metadata URL</label>
              <textarea
                id="metadataUrl"
                rows="1"
                formControlName="METADATA_AUTO_CONF_URL"
                [readonly]="ssoSharedAcrossTenants"
                required
              ></textarea>
              <div class="help">
                Paste the metadata URL from your IdP, e.g. Okta or Azure AD.
              </div>
            </div>

            <!-- Service provider details -->
            <div class="sp-card">
              <div class="h">Service provider details</div>
              <div class="sub">
                Give this Entity ID to your IT team when setting up the IdP.
              </div>

              <div class="ro-label">Entity ID</div>
              <div class="ro-field">
                <span class="v">{{ entityId }}</span>
                <button
                  type="button"
                  class="cp"
                  (click)="copy(entityId)"
                  aria-label="Copy Entity ID"
                >
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
            </div>

            <!-- Success banner -->
            <div class="ok-banner" *ngIf="saml_config?.is_active">
              <mat-icon>check_circle</mat-icon>
              Single sign-on is configured and active.
            </div>

            <!-- Actions -->
            <div class="actions-row" *ngIf="!ssoSharedAcrossTenants">
              <button type="submit" class="btn-primary">Save</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  `,
  styleUrls: ['./saml-signon.component.scss'],
})
export class SamlSignOnComponent implements OnInit {
  saml_config: any;
  ssoSharedAcrossTenants: boolean = false;
  formData: any = this.formBuilder.group({
    METADATA_AUTO_CONF_URL: '',
    ATTRIBUTES_MAP: this.formBuilder.group({
      email: 'Email',
      username: 'Email',
      first_name: 'FirstName',
      last_name: 'LastName',
    }),
    ASSERTION_URL: '',
    ENTITY_ID: '',
  });

  entityId: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private snackBar: SharedFeaturesService,
    private settingServices: SettingsService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const protocol = this.getProtocolFromUrl(window.location.href);
    this.entityId = protocol + '://' + window.location.host + '/saml2_auth/acs';
    this.formData.get('ENTITY_ID').setValue(this.entityId);
    this.formData
      .get('ASSERTION_URL')
      .setValue(protocol + '://' + window.location.host);
    this.settingServices.getSamlStatus().subscribe({
      next: (response: any) => {
        // Check if SSO is shared across tenants
        this.ssoSharedAcrossTenants = response?.sso_shared_across_tenants || false;
        
        if (this.ssoSharedAcrossTenants) {
          // Disable form when SSO is shared
          this.formData.disable();
          this.snackBar.openSnackBar(
            'SSO is managed globally and cannot be modified at tenant level',
            'Close',
            'warning-snackbar'
          );
        } else if (response?.saml) {
          this.snackBar.openSnackBar(
            'Saml has been already configured',
            'Close',
            'success-snackbar'
          );
        }
      },
      error: (error: any) => {
        console.error('An error occurred', error);
      },
    });
    this.getSamlConfig();
  }

  getSamlConfig() {
    this.settingServices.getSamlConfiguration().subscribe({
      next: (response: any) => {
        this.formData
          .get('METADATA_AUTO_CONF_URL')
          .setValue(response?.msg?.METADATA_AUTO_CONF_URL);
        this.saml_config = response?.msg;
      },
      error: (error: any) => {
        console.error('An error occurred', error);
      },
    });
  }

  submitForm() {
    // Prevent submission if SSO is shared across tenants
    if (this.ssoSharedAcrossTenants) {
      this.snackBar.openSnackBar(
        'Cannot modify SSO configuration - it is managed globally',
        'Close',
        'error-snackbar'
      );
      return;
    }

    const formDataValue = this.formData.value;
    formDataValue.ATTRIBUTES_MAP.username = formDataValue.ATTRIBUTES_MAP.email;
    this.settingServices.saveOrUpdateSamlConfigration(formDataValue).subscribe({
      next: (response: any) => {
        this.snackBar.openSnackBar(
          response?.message,
          'Close',
          'success-snackbar'
        );
      },
      error: (error: any) => {
        console.error('An error occurred', error);
        this.snackBar.openSnackBar(
          error?.error?.message || error?.message,
          'Close',
          'error-snackbar'
        );
      },
    });
  }

  copy(value: string) {
    navigator.clipboard.writeText(value);
    this.snackBar.openSnackBar('Copied', 'Close', 'success-snackbar');
  }

  getProtocolFromUrl(url: string): string | null {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol.replace(':', '');
    } catch (error) {
      console.error('Error parsing URL:', error);
      return null;
    }
  }

  activateDeactivateSso(data: any) {
    // Prevent activation/deactivation if SSO is shared across tenants
    if (this.ssoSharedAcrossTenants) {
      this.snackBar.openSnackBar(
        'Cannot modify SSO status - it is managed globally',
        'Close',
        'error-snackbar'
      );
      return;
    }

    const turningOn = !!data?.is_active;
    const ref = this.dialog.open(ShowDialogsComponent, {
      data: {
        title: turningOn
          ? 'Turn on single sign-on?'
          : 'Turn off single sign-on?',
        message: turningOn
          ? 'Your whole team will sign in through your identity provider. Make sure your SAML details are correct first.'
          : 'Your team will no longer be able to sign in with SSO and will need their email and password.',
        confirmText: turningOn ? 'Turn on' : 'Turn off',
        cancelText: 'Cancel',
        danger: !turningOn,
      },
      disableClose: false,
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.settingServices.deactivateSSO(data).subscribe({
        next: (response: any) => {
          this.getSamlConfig();
          this.snackBar.openSnackBar(response?.msg, 'Close', 'success-snackbar');
        },
        error: (error: any) => {
          console.error('An error occurred', error);
          this.snackBar.openSnackBar(
            error?.error?.message || error?.message,
            'Close',
            'error-snackbar'
          );
        },
      });
    });
  }
}
